import moment from 'moment';
import React, { useCallback, useContext, useState } from 'react';
import ReactDOM from 'react-dom';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import DataTable from 'components/DataTable';
import DateTimePicker from 'components/DateTimePicker';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import ExportModal from 'components/ExportModal';
import Toggle from 'components/FormFields/Toggle';
import { ApproveModal } from 'components/Modal/ApproveModal';
import PageHeaderIcon from 'components/PageHeaderIcon';
import QuickActions from 'components/QuickActions';
import SearchInput from 'components/SearchInput';
import TableActionButton from 'components/TableActionButton';
import ThemedSelect from 'components/ThemedSelect';
import Toaster from 'components/Toaster';
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import {
  FilterOverlay,
  FilterDropdown,
  FilterSection,
  FilterTitle,
  FilterRange,
  FilterActions,
} from 'pages/track/Customers/components/CustomerTable/StyledComponents';
import { AppContext } from 'shared/AppContext';
import { formatPhoneNumber } from 'shared/formatters';
import { useGetAccountPurchaseReport } from '../Purchases/gql/useGetAccountPurchaseReport.mutation';
import { PrintAnnualPurchaseReport } from '../Purchases/PrintAnnualPurchaseReport';
import {
  Sidebar,
  WaiverBadge,
} from '../Purchases/PurchaseDetailPage/StyledComponents';
import {
  PageWrapper,
  Container,
  DashboardLayout,
  MainContent,
  HeaderContainer,
  HeaderTitle,
  ContentWrapper,
  PageHeader,
  FilterRow,
  FilterContainer,
} from '../Purchases/PurchasesPage/StyledComponents';
import {
  useApproveMember,
  useUnApproveMember,
} from './gql/useApproveMemberships';
import { useGetAccountMembers } from './gql/useGetAccountMembers';
import { useGetAccountMemberships } from './gql/useGetAccountMemberships';
import { useGetAccountMembersReport } from './gql/useGetAccountMembersReport';
import useMembersExport from './hooks/useMembersExport';

// Convert YYYY-MM-DD to MM/DD/YYYY for the GraphQL Date scalar
const toGraphQLDate = (dateStr) => {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-');
  return `${m}-${d}-${y}`;
};

// Receipt icon for page header
const MembersIcon = () => (
  <svg
    width="24"
    height="24"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
    />
  </svg>
);

const MembersPageContent = () => {
  const history = useHistory();
  const location = useLocation();
  const { pathname } = location;
  const { id: accountIdParam, name: accountName } = useParams();

  // Context-aware routing
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const {
    state: { accountId: contextAccountId },
  } = useContext(AppContext);

  // Use URL param for regular admin, AppContext for track/employee
  const accountId = accountIdParam
    ? parseInt(accountIdParam)
    : contextAccountId || null;
  const displayName = accountName ? decodeURIComponent(accountName) : null;

  // Get current year
  const currentYear = Number(moment().format('YYYY'));

  // Filter state
  const [year, setYear] = useState(currentYear);
  const [membershipFilter, setMembershipFilter] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [pendingStartDate, setPendingStartDate] = useState('');
  const [pendingEndDate, setPendingEndDate] = useState('');

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  const hasActiveFilters = !!(startDate || endDate);

  const toggleFilterDropdown = () => setFilterOpen(!filterOpen);

  const clearFilters = () => {
    setPendingStartDate('');
    setPendingEndDate('');
    setStartDate('');
    setEndDate('');
    setFilterOpen(false);
  };

  const applyFilters = () => {
    setStartDate(pendingStartDate);
    setEndDate(pendingEndDate);
    setFilterOpen(false);
  };

  // Search and pagination
  const { input } = useSearchInput();
  const currentPage = input.page ? Number(input.page) : 1;
  const updateQuery = useUpdateQuery();

  // Get memberships for filter dropdown
  const { data: membershipsData } = useGetAccountMemberships({
    year,
    accountId,
  });
  const memberships = membershipsData?.getAccountMemberships ?? [];

  // Fetch members
  const { data, loading } = useGetAccountMembers({
    account_id: accountId,
    year,
    page: currentPage,
    limit: 15,
    queryString: input.queryString || null,
    ticket_id: membershipFilter ? Number(membershipFilter.value) : null,
    start_date: toGraphQLDate(startDate),
    end_date: toGraphQLDate(endDate),
  });

  const members = data?.getAccountMembers?.results ?? [];
  const totalCount = data?.getAccountMembers?.count ?? 0;
  const totalPages = Math.ceil(totalCount / 15);

  // Year options
  const yearOptions = [
    { value: currentYear - 2, label: String(currentYear - 2) },
    { value: currentYear - 1, label: String(currentYear - 1) },
    { value: currentYear, label: String(currentYear) },
    { value: currentYear + 1, label: String(currentYear + 1) },
  ];

  // Membership options for filter
  const membershipOptions = memberships.map((m) => ({
    value: m.id,
    label: m.name,
  }));

  // Handlers
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  const handleSearch = (searchTerm) => {
    updateQuery({ queryString: searchTerm, page: 1 });
  };

  // Approve/unapprove mutations
  const approveMember = useApproveMember();
  const unApproveMember = useUnApproveMember();
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [pendingMember, setPendingMember] = useState(null);

  const handleToggleClick = (member) => {
    setPendingMember(member);
    setApproveModalVisible(true);
  };

  const handleConfirmApproval = async () => {
    if (!pendingMember) return;
    if (pendingMember.approved) {
      return unApproveMember(pendingMember.id);
    }
    return approveMember(pendingMember.id);
  };

  // PDF Export (used by regular admin)
  const getAccountPurchaseReport = useGetAccountPurchaseReport({
    account_id: accountId,
    type: 'membership',
    start_date: toGraphQLDate(startDate),
    end_date: toGraphQLDate(endDate),
  });

  // Old synchronous export (used by regular admin)
  const [isExportingOld, setIsExportingOld] = useState(false);
  const getAccountMembersReport = useGetAccountMembersReport({
    account_id: accountId,
    year,
    queryString: input.queryString || null,
    ticket_id: membershipFilter ? Number(membershipFilter.value) : null,
    type: 'membership',
    start_date: toGraphQLDate(startDate),
    end_date: toGraphQLDate(endDate),
  });

  const handleOldExport = async () => {
    try {
      setIsExportingOld(true);
      const response = await getAccountMembersReport();

      if (!response || response.errors) {
        toast.error('Members list could not be retrieved');
        setIsExportingOld(false);
        return;
      }

      const results = response.data?.getAccountMembersReport?.results ?? [];

      const dateTimeStamp = moment().format('MMM DD - YYYY h:mm A');
      const headers = 'Name,Email,Phone,Membership,Waiver Status\n';
      const rows = results
        .map((member) => {
          const name = member.user?.formatted_name || '';
          const email = member.user?.email || '';
          const phone = member.user?.cellphone
            ? formatPhoneNumber(member.user.cellphone)
            : '';
          const membership = member.membership?.name || '';
          const waiver = member.is_signed ? 'Signed' : 'Not Signed';
          return `"${name}","${email}","${phone}","${membership}","${waiver}"`;
        })
        .join('\n');

      const csv = `Members List\nExported:,${dateTimeStamp}\nAccount:,${
        displayName || ''
      }\n\n${headers}${rows}`;
      const filename = `${moment().format('MMM DD - YYYY').toUpperCase()} ${
        displayName || 'Members'
      } - Members List.csv`;

      var element = document.createElement('a');
      element.setAttribute(
        'href',
        'data:text/plain;charset=utf-8,' + encodeURIComponent(csv)
      );
      element.setAttribute('download', filename);
      element.style.display = 'none';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);

      setIsExportingOld(false);
    } catch (e) {
      setIsExportingOld(false);
    }
  };

  // Queue-based export (used by track/employee admin)
  const { startExport, subscribeToProgress, getDownloadUrl, isExporting } =
    useMembersExport();
  const [showExportModal, setShowExportModal] = useState(false);
  const [toasterData, setToasterData] = useState(null);

  const handleExport = useCallback(
    async (format) => {
      setShowExportModal(false);

      setToasterData({
        title: `Exporting to ${format}...`,
        message: 'Preparing your export file',
        type: 'info',
        persistent: true,
      });

      try {
        const jobId = await startExport(
          {
            accountId,
            year,
            typeId: membershipFilter ? membershipFilter.value : null,
            search: input.queryString || null,
            startDate: toGraphQLDate(startDate),
            endDate: toGraphQLDate(endDate),
            type: 'membership',
          },
          format
        );

        subscribeToProgress(jobId, {
          onProgress: (status) => {
            if (status.progress && status.recordCount) {
              setToasterData({
                title: `Exporting to ${format}...`,
                message: `Processing ${status.recordCount} members (${status.progress}%)`,
                type: 'info',
                persistent: true,
              });
            }
          },
          onComplete: async (data) => {
            try {
              const downloadUrl = await getDownloadUrl(jobId);

              setToasterData({
                title: 'Export Ready!',
                message: `${
                  data.recordCount || 0
                } members exported to ${format}`,
                type: 'success',
                persistent: true,
                action: {
                  text: `Download ${format}`,
                  icon: 'download',
                  onClick: () => {
                    window.open(downloadUrl, '_blank');
                  },
                },
              });
            } catch (error) {
              setToasterData({
                title: 'Download Error',
                message: 'Failed to generate download link. Please try again.',
                type: 'error',
              });
            }
          },
          onError: (error) => {
            let errorMessage =
              'An error occurred while exporting. Please try again.';

            if (error.message?.includes('permission')) {
              errorMessage =
                'You do not have permission to export from this account';
            } else if (error.message?.includes('not found')) {
              errorMessage = 'Export not found or has expired';
            }

            setToasterData({
              title: 'Export Failed',
              message: errorMessage,
              type: 'error',
            });
          },
        });
      } catch (error) {
        setToasterData({
          title: 'Export Failed',
          message: 'Could not start export. Please try again.',
          type: 'error',
        });
      }
    },
    [
      startExport,
      subscribeToProgress,
      getDownloadUrl,
      accountId,
      year,
      membershipFilter,
      input.queryString,
      startDate,
      endDate,
    ]
  );

  // Table columns
  const columns = [
    ...(isTrackAdmin || isEmployeeAdmin
      ? [
          {
            field: 'approved',
            header: 'Approved',
            render: (member) => (
              <Toggle
                checked={!!member.approved}
                onChange={() => handleToggleClick(member)}
              />
            ),
          },
        ]
      : []),
    {
      field: 'name',
      header: 'Name',
      render: (member) => member.user?.formatted_name || '—',
    },
    {
      field: 'email',
      header: 'Email',
      render: (member) => member.user?.email || '—',
    },
    {
      field: 'phone',
      header: 'Phone',
      render: (member) =>
        member.user?.cellphone ? (
          <span style={{ whiteSpace: 'nowrap' }}>
            {formatPhoneNumber(member.user.cellphone)}
          </span>
        ) : (
          '—'
        ),
    },
    {
      field: 'membership',
      header: 'Membership',
      render: (member) => member.membership?.name || '—',
    },
    {
      field: 'agreements',
      header: 'Agreements',
      render: (member) => (
        <WaiverBadge $signed={member.is_signed}>
          {member.is_signed ? 'Signed' : 'Not Signed'}
        </WaiverBadge>
      ),
    },
  ];

  // Table actions
  const actions = [
    {
      type: 'view',
      title: 'View Details',
      onClick: (member) => {
        if (member.id) {
          history.push(
            `${routePrefix}/annualMembersDetail/member/${member.id}`
          );
        }
      },
    },
    {
      type: 'credit',
      title: 'View Transaction',
      onClick: (member) => {
        if (member.purchase?.id) {
          history.push(
            `${routePrefix}/transactions/detail/${member.purchase.id}`
          );
        }
      },
    },
  ];

  return (
    <PageWrapper>
      <Container>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderContainer>
            <PageHeaderIcon>
              <MembersIcon />
            </PageHeaderIcon>
            <HeaderTitle>
              {displayName ? `${displayName} - Members` : 'Members'}
            </HeaderTitle>
          </HeaderContainer>
        </Card>

        {/* Dashboard Layout */}
        <DashboardLayout>
          <MainContent>
            {/* Data Table Card */}
            <Card noPadding>
              <ContentWrapper>
                {/* Search + Filters Row */}
                <div style={{ position: 'relative' }}>
                  <PageHeader>
                    <SearchInput
                      onSearch={handleSearch}
                      placeholder="Search by name..."
                    />
                    <FilterRow>
                      <FilterContainer>
                        <ThemedSelect
                          placeholder="Year"
                          options={yearOptions}
                          value={{ value: year, label: String(year) }}
                          onChange={(selected) =>
                            setYear(selected?.value || currentYear)
                          }
                        />
                      </FilterContainer>
                      {memberships.length > 0 && (
                        <FilterContainer>
                          <ThemedSelect
                            placeholder="All Memberships"
                            options={membershipOptions}
                            value={membershipFilter}
                            onChange={(selected) =>
                              setMembershipFilter(selected || null)
                            }
                            isClearable
                          />
                        </FilterContainer>
                      )}
                      <TableActionButton
                        onFilterClick={toggleFilterDropdown}
                        onClearClick={clearFilters}
                        hasActiveFilters={hasActiveFilters}
                      />
                    </FilterRow>
                  </PageHeader>

                  {isMobile &&
                    filterOpen &&
                    ReactDOM.createPortal(
                      <>
                        <FilterOverlay
                          show={filterOpen}
                          onClick={toggleFilterDropdown}
                        />
                        <FilterDropdown style={{ zIndex: 10001 }}>
                          <div>
                            <FilterSection>
                              <FilterTitle>Date Range</FilterTitle>
                              <FilterRange>
                                <div style={{ flex: 1 }}>
                                  <DateTimePicker
                                    value={pendingStartDate || null}
                                    onChange={(date) =>
                                      setPendingStartDate(date || '')
                                    }
                                    placeholder="Start date"
                                    showTime={false}
                                    cancelText="Clear"
                                  />
                                </div>
                                <span>to</span>
                                <div style={{ flex: 1 }}>
                                  <DateTimePicker
                                    value={pendingEndDate || null}
                                    onChange={(date) =>
                                      setPendingEndDate(date || '')
                                    }
                                    placeholder="End date"
                                    showTime={false}
                                    cancelText="Clear"
                                    minDate={
                                      pendingStartDate
                                        ? new Date(
                                            pendingStartDate + 'T00:00:00'
                                          )
                                        : null
                                    }
                                  />
                                </div>
                              </FilterRange>
                            </FilterSection>
                            <FilterActions>
                              <button onClick={clearFilters}>Clear</button>
                              <button
                                className="btn-primary"
                                onClick={applyFilters}
                              >
                                Apply Filters
                              </button>
                            </FilterActions>
                          </div>
                        </FilterDropdown>
                      </>,
                      document.body
                    )}

                  {filterOpen && !isMobile && (
                    <FilterDropdown>
                      <div>
                        <FilterSection>
                          <FilterTitle>Date Range</FilterTitle>
                          <FilterRange>
                            <div style={{ flex: 1 }}>
                              <DateTimePicker
                                value={pendingStartDate || null}
                                onChange={(date) =>
                                  setPendingStartDate(date || '')
                                }
                                placeholder="Start date"
                                showTime={false}
                                cancelText="Clear"
                              />
                            </div>
                            <span>to</span>
                            <div style={{ flex: 1 }}>
                              <DateTimePicker
                                value={pendingEndDate || null}
                                onChange={(date) =>
                                  setPendingEndDate(date || '')
                                }
                                placeholder="End date"
                                showTime={false}
                                cancelText="Clear"
                                minDate={
                                  pendingStartDate
                                    ? new Date(pendingStartDate + 'T00:00:00')
                                    : null
                                }
                              />
                            </div>
                          </FilterRange>
                        </FilterSection>
                        <FilterActions>
                          <button onClick={clearFilters}>Clear</button>
                          <button
                            className="btn-primary"
                            onClick={applyFilters}
                          >
                            Apply Filters
                          </button>
                        </FilterActions>
                      </div>
                    </FilterDropdown>
                  )}
                </div>

                <DataTable
                  columns={columns}
                  data={members}
                  loading={loading}
                  keyField="id"
                  emptyMessage="No members found"
                  emptySubtext="Try adjusting your filters"
                  emptyIcon={
                    <svg
                      width="48"
                      height="48"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      style={{ marginBottom: '16px', opacity: 0.5 }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.5"
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  }
                  actions={actions}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </ContentWrapper>
            </Card>
          </MainContent>

          <Sidebar>
            {isTrackAdmin || isEmployeeAdmin ? (
              <QuickActions
                title="Export"
                actions={[
                  {
                    label: 'Export Members',
                    disabled: isExporting || totalCount === 0,
                    disabledReason: isExporting
                      ? 'Export in progress...'
                      : 'No data to export',
                    icon: (
                      <svg
                        width="16"
                        height="16"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                      >
                        <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                        <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
                      </svg>
                    ),
                    onClick: () => setShowExportModal(true),
                  },
                ]}
              />
            ) : (
              <QuickActions
                title="Export"
                actions={[
                  {
                    label: 'Export CSV',
                    disabled: isExportingOld || totalCount === 0,
                    disabledReason: isExportingOld
                      ? 'Export in progress...'
                      : 'No data to export',
                    icon: (
                      <svg
                        width="16"
                        height="16"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                      >
                        <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                        <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
                      </svg>
                    ),
                    onClick: () => handleOldExport(),
                  },
                  {
                    component: (
                      <PrintAnnualPurchaseReport
                        annualTicketName={displayName || 'Members'}
                        accountName={displayName || 'Members'}
                        account_id={accountId}
                        fetchReport={getAccountPurchaseReport}
                        responseKey="getAccountPurchaseReport"
                        button={
                          <ButtonV2
                            elevated
                            icon={
                              <svg
                                width="16"
                                height="16"
                                fill="currentColor"
                                viewBox="0 0 16 16"
                              >
                                <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                                <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
                              </svg>
                            }
                          >
                            Export PDF
                          </ButtonV2>
                        }
                      />
                    ),
                  },
                ]}
              />
            )}
          </Sidebar>
        </DashboardLayout>
      </Container>

      <ApproveModal
        isVisible={approveModalVisible}
        setIsVisible={setApproveModalVisible}
        itemType="member"
        unCheck={pendingMember?.approved}
        onConfirm={handleConfirmApproval}
      />

      {(isTrackAdmin || isEmployeeAdmin) && (
        <>
          {toasterData && (
            <Toaster
              isOpen={!!toasterData}
              onClose={() => setToasterData(null)}
              title={toasterData.title}
              message={toasterData.message}
              type={toasterData.type}
              duration={toasterData.persistent ? 25000 : 10000}
              persistent={toasterData.persistent}
              action={toasterData.action}
            />
          )}

          <ExportModal
            isOpen={showExportModal}
            onClose={() => setShowExportModal(false)}
            onExport={handleExport}
            isExporting={isExporting}
            recordCount={totalCount}
            dataType="members"
          />
        </>
      )}
    </PageWrapper>
  );
};

// Wrap with theme provider
const MembersPage = () => (
  <EnhancedThemeProvider>
    <MembersPageContent />
  </EnhancedThemeProvider>
);

export default MembersPage;
